<template>
  <div class="console-container">
    <!-- 页面标题区域 -->
    <div class="console-header">
      <h1 class="console-title">控制台</h1>
      <p class="console-subtitle">容一小程序管理控制台 - 系统概览与快速操作</p>
    </div>

    <!-- 统计卡片区域 -->
    <div class="stats-section">
      <CanvasGlassCard
        v-for="(stat, index) in systemStats" 
        :key="index"
        :title="stat.title"
        variant="primary"
        size="medium"
        class="stat-card"
      >
        <div class="stat-content">
          <div class="stat-icon">
            <component :is="stat.icon" />
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-change" :class="stat.trend">
              {{ stat.change }}
            </div>
          </div>
        </div>
      </CanvasGlassCard>
    </div>

    <!-- 主要内容区域 -->
    <div class="console-main">
      <!-- 左侧区域 -->
      <div class="console-left">
        <!-- 快速操作面板 -->
        <CanvasGlassCard title="快速操作" variant="secondary" class="quick-actions-card">
          <div class="quick-actions">
            <ZlButton 
              v-for="(action, index) in quickActions" 
              :key="index"
              :variant="action.variant"
              glass
              class="action-btn"
              @click="handleQuickAction(action.key)"
            >
              <template #icon>
                <component :is="action.icon" />
              </template>
              {{ action.label }}
            </ZlButton>
          </div>
        </CanvasGlassCard>

        <!-- 系统监控 -->
        <CanvasGlassCard title="系统监控" variant="primary" class="monitor-card">
          <div class="monitor-grid">
            <div class="monitor-item" v-for="(item, index) in monitorData" :key="index">
              <div class="monitor-label">{{ item.label }}</div>
              <div class="monitor-progress">
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ width: item.value + '%', backgroundColor: getProgressColor(item.value) }"
                  ></div>
                </div>
                <span class="progress-text">{{ item.value }}%</span>
              </div>
            </div>
          </div>
        </CanvasGlassCard>
      </div>

      <!-- 右侧区域 -->
      <div class="console-right">
        <!-- 最近活动 -->
        <CanvasGlassCard title="最近活动" variant="light" class="activity-card">
          <div class="activity-list">
            <div 
              v-for="(activity, index) in recentActivities" 
              :key="index"
              class="activity-item"
            >
              <div class="activity-icon" :class="activity.type">
                <component :is="activity.icon" />
              </div>
              <div class="activity-content">
                <div class="activity-title">{{ activity.title }}</div>
                <div class="activity-time">{{ activity.time }}</div>
              </div>
            </div>
          </div>
        </CanvasGlassCard>

        <!-- 系统信息 -->
        <CanvasGlassCard title="系统信息" variant="secondary" class="system-info-card">
          <div class="system-info">
            <div class="info-item" v-for="(info, index) in systemInfo" :key="index">
              <span class="info-label">{{ info.label }}:</span>
              <span class="info-value">{{ info.value }}</span>
            </div>
          </div>
        </CanvasGlassCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted} from 'vue'
import { message } from '../utils/message'
import { getSystemMonitor, getSystemMonitorStream } from '@/apis/system-monitor'
import CanvasGlassCard from '@/components/CanvasGlassCard.vue'
import ZlButton from '@/components/ZlButton.vue'
import { 
  IconUser, 
  IconStorage, 
  IconDesktop, 
  IconFile, 
  IconPlus, 
  IconSettings, 
  IconRefresh, 
  IconExport,
  IconCheck,
  IconExclamation,
  IconInfo
} from '@arco-design/web-vue/es/icon'

// 系统统计数据
const systemStats = ref([
  {
    title: '总用户数',
    value: '1,234',
    change: '+12.5%',
    trend: 'positive',
    icon: 'IconUser'
  },
  {
    title: '存储使用',
    value: '68.5GB',
    change: '+2.1GB',
    trend: 'neutral',
    icon: 'IconStorage'
  },
  {
    title: '在线设备',
    value: '156',
    change: '+8',
    trend: 'positive',
    icon: 'IconDesktop'
  }
])

// 快速操作
const quickActions = ref([
  { key: 'add-user', label: '添加用户', icon: 'IconPlus', variant: 'primary' },
  { key: 'system-settings', label: '系统设置', icon: 'IconSettings', variant: 'secondary' },
  { key: 'refresh-data', label: '刷新数据', icon: 'IconRefresh', variant: 'info' },
  { key: 'export-data', label: '导出数据', icon: 'IconExport', variant: 'success' }
])

// 系统监控数据
const monitorData = ref([
  { label: 'CPU使用率', value: 32 },
  { label: '内存使用率', value: 68 },
  { label: '磁盘使用率', value: 55 },
  { label: '网络带宽', value: 23 }
])

// 最近活动
const recentActivities = ref([
  {
    title: '用户张三登录系统',
    time: '2分钟前',
    type: 'success',
    icon: 'IconCheck'
  },
  {
    title: '系统备份完成',
    time: '15分钟前',
    type: 'info',
    icon: 'IconInfo'
  },
  {
    title: '磁盘空间不足警告',
    time: '1小时前',
    type: 'warning',
    icon: 'IconExclamation'
  },
  {
    title: '新用户注册',
    time: '2小时前',
    type: 'success',
    icon: 'IconUser'
  }
])

// 运行时间
const runningTime = ref('')
// 通过当前时间和运行时间计算运行时间
const calculateRunningTime = () => {
  const currentTime = new Date()
  const startTime = new Date('2025-08-01 00:00:00')
  const diff = currentTime - startTime
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((diff / (1000 * 60)) % 60)
  const seconds = Math.floor((diff / 1000) % 60)
  return `${days}天${hours}小时${minutes}分钟${seconds}秒`
}

const requestSystemMonitor = () =>{
  getSystemMonitor().then(res=>{
    if(res.code === 0){
      monitorData.value = [
        {label: 'CPU使用率', value: res.data.cpu.usagePercent },
        {label: '内存使用率', value: res.data.memory.usagePercent },
        {label: '磁盘使用率', value: res.data.disks[0].usagePercent },
        {label: '网络带宽', value: Number((res.data.networks[0].bytesReceived/res.data.networks[0].speed).toFixed(2))}
      ]
    }
  })
}
// 使用SSE监听系统监控数据
const getSystemMonitorData = () => {
  requestSystemMonitor();
  const eventSource = getSystemMonitorStream()
  
  // 添加open事件处理器来确认连接建立
  eventSource.onopen = () => {
    console.log('SSE连接已建立')
  }


  
  // 使用addEventListener确保事件监听正确绑定
  eventSource.addEventListener('system-monitor', (event) => {
    try {
      console.log('原始SSE数据:', event.data)
      const data = JSON.parse(event.data)
      console.log('解析后的数据:', data)
      
      monitorData.value = [
        {label: 'CPU使用率', value: data.cpu.usagePercent },
        {label: '内存使用率', value: data.memory.usagePercent },
        {label: '磁盘使用率', value: data.disks[0].usagePercent },
        {label: '网络带宽', value: Number((data.networks[0].bytesReceived/data.networks[0].speed).toFixed(2))}
      ]
    } catch (err) {
      console.error('数据解析错误:', err)
    }
  })

  eventSource.onerror = (error) => {
    console.error('SSE连接错误:', error)
    // 尝试重新连接
    setTimeout(() => {
      console.log('尝试重新连接...')
      eventSource.close()
      // getSystemMonitorData()
    }, 5000)
  }

  // 组件卸载时关闭SSE连接
  onUnmounted(() => {
    if (eventSource) {
      console.log('关闭SSE连接')
      eventSource.close()
    }
  })
}

// getSystemMonitorData()
setInterval(() => {
  systemInfo.value[1].value = calculateRunningTime()
}, 1000);


// 系统信息
const systemInfo = ref([
  { label: '系统版本', value: 'v1.1.0' },
  { label: '运行时间', value: calculateRunningTime() },
  { label: '数据库', value: 'Postgres 17.0' },
  { label: '服务器', value: 'Ubuntu 22.04' }
])

// 处理快速操作点击
const handleQuickAction = (key) => {
  message.error('操作失败', '暂未开启该功能')

  console.log('Quick action clicked:', key)
  // 这里可以添加具体的操作逻辑
}

// 获取进度条颜色
const getProgressColor = (value) => {
  if (value < 30) return '#52c41a'
  if (value < 70) return '#faad14'
  return '#ff4d4f'
}

// 组件挂载时的初始化
onMounted(() => {
  console.log('控制台页面已加载')
})
</script>

<style scoped>
.console-container {
  padding: 24px;
  min-height: 100vh;
  background: transparent;
  overflow-y: auto;
}

.console-header {
  margin-bottom: 32px;
  text-align: center;
}

.console-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #4F46E5, #7C3AED);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.console-subtitle {
  font-size: 16px;
  color: #7f8c8d;
  margin: 0;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #94A3B8, #CBD5E1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
}

.stat-change {
  font-size: 14px;
  font-weight: 500;
}

.stat-change.positive {
  color: #27ae60;
}

.stat-change.negative {
  color: #e74c3c;
}

.stat-change.neutral {
  color: #7f8c8d;
}

.console-main {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.console-left,
.console-right {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.action-btn {
  height: 60px;
  font-size: 14px;
}

.monitor-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.monitor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.monitor-label {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.monitor-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 200px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #ecf0f1;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #7f8c8d;
  min-width: 35px;
  text-align: right;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 300px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.5);
  transition: background 0.3s ease;
}

.activity-item:hover {
  background: rgba(255, 255, 255, 0.8);
}

.activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
}

.activity-icon.success {
  background: #27ae60;
}

.activity-icon.warning {
  background: #f39c12;
}

.activity-icon.info {
  background: #3498db;
}

.activity-content {
  flex: 1;
}

.activity-title {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
  margin-bottom: 2px;
}

.activity-time {
  font-size: 12px;
  color: #7f8c8d;
}

.system-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 14px;
  color: #7f8c8d;
}

.info-value {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .console-main {
    grid-template-columns: 1fr;
  }
  
  .stats-section {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

@media (max-width: 768px) {
  .console-container {
    padding: 16px;
  }
  
  .console-title {
    font-size: 24px;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .quick-actions {
    grid-template-columns: 1fr;
  }
}
</style>